/**
 * Compact Product Card — Complementary / "Complete the Look" block
 * -------------------------------------------------------------------
 * Loaded once globally via layout/theme.liquid. Has to work for cards that
 * exist at first paint AND for cards injected later by Dawn's
 * <complementary-products> element, which fetches its markup via the
 * Section Rendering API and drops it in with innerHTML *after* the page
 * has already loaded. Every listener below is delegated to `document` so
 * it keeps working no matter when the card shows up in the DOM.
 */
(function () {
  'use strict';

  var SELECTORS = {
    card: '.comp-cstm-product-card',
    form: '.comp-cstm-product-card__inner-form',
    sizeOption: '.comp-cstm-size-option',
    sizeError: '.comp-cstm-product-card__size-error',
    formError: '.comp-cstm-product-card__form-error',
    variantInput: '.comp-cstm-product-card__variant-id',
    priceWrap: '.comp-cstm-product-card__price',
    atcBtn: '.comp-cstm-product-card__atc-btn',
  };

  /* ----------------------------------------------------------- */
  /* 1. Size selection                                            */
  /* ----------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    var sizeBtn = event.target.closest(SELECTORS.sizeOption);
    if (!sizeBtn || sizeBtn.disabled) return;

    var card = sizeBtn.closest(SELECTORS.card);
    if (!card) return;

    selectSize(card, sizeBtn);
  });

  function selectSize(card, sizeBtn) {
    card.querySelectorAll(SELECTORS.sizeOption).forEach(function (btn) {
      btn.classList.remove('comp-cstm-size-option--selected');
      btn.setAttribute('aria-pressed', 'false');
    });

    sizeBtn.classList.add('comp-cstm-size-option--selected');
    sizeBtn.setAttribute('aria-pressed', 'true');

    var variantInput = card.querySelector(SELECTORS.variantInput);
    if (variantInput) variantInput.value = sizeBtn.dataset.variantId;

    updatePrice(
      card,
      sizeBtn.dataset.price,
      sizeBtn.dataset.comparePrice,
      sizeBtn.dataset.onSale === 'true'
    );

    var sizeError = card.querySelector(SELECTORS.sizeError);
    if (sizeError) sizeError.hidden = true;
  }

  function updatePrice(card, price, comparePrice, onSale) {
    var priceWrap = card.querySelector(SELECTORS.priceWrap);
    if (!priceWrap) return;

    priceWrap.innerHTML = onSale
      ? '<span class="comp-cstm-price comp-cstm-price--compare">' + comparePrice + '</span>' +
        '<span class="comp-cstm-price comp-cstm-price--sale">' + price + '</span>'
      : '<span class="comp-cstm-price">' + price + '</span>';
  }

  /* ----------------------------------------------------------- */
  /* 2. Add to cart (AJAX) — synced with cart-drawer / cart-notification */
  /* ----------------------------------------------------------- */

  document.addEventListener('submit', function (event) {
    var form = event.target.closest(SELECTORS.form);
    if (!form) return;

    // Without this the browser falls back to a plain GET to the current
    // URL (no action/method on this form) — that was the reload bug.
    event.preventDefault();

    addToCart(form);
  });

  function getActiveCartUI() {
    // Your theme ships both components; whichever is actually rendered on
    // the page (per your "use notification" setting) is the one we drive.
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function addToCart(form) {
    var card = form.closest(SELECTORS.card);
    var atcBtn = form.querySelector(SELECTORS.atcBtn);
    var variantInput = form.querySelector(SELECTORS.variantInput);
    var sizeError = card.querySelector(SELECTORS.sizeError);
    var formError = card.querySelector(SELECTORS.formError);

    if (!variantInput || !variantInput.value) return;

    if (atcBtn && atcBtn.dataset.requiresSize === 'true') {
      var hasSelectedSize = card.querySelector('.comp-cstm-size-option--selected');
      if (!hasSelectedSize) {
        if (sizeError) sizeError.hidden = false;
        return;
      }
    }

    if (formError) formError.hidden = true;
    setButtonState(atcBtn, 'loading');

    var cartUI = getActiveCartUI();
    var body = {
      id: variantInput.value,
      quantity: 1,
    };

    // Request exactly the sections cart-drawer.js / cart-notification.js
    // expect, in this same call — this is what lets their existing
    // renderContents() work without any changes on your end.
    if (cartUI && typeof cartUI.getSectionsToRender === 'function') {
      body.sections = cartUI
        .getSectionsToRender()
        .map(function (section) {
          return section.id;
        })
        .join(',');
      body.sections_url = window.location.pathname;
    }

    fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (data.status) {
          throw new Error(data.description || 'Could not add this item to your cart.');
        }

        setButtonState(atcBtn, 'added');

        if (cartUI && typeof cartUI.renderContents === 'function') {
          try {
            cartUI.renderContents(data);
          } catch (renderError) {
            refreshCartCountFallback();
          }
        } else {
          refreshCartCountFallback();
        }
      })
      .catch(function (error) {
        setButtonState(atcBtn, 'default');
        if (formError) {
          formError.textContent = error.message;
          formError.hidden = false;
        }
      });
  }

  function refreshCartCountFallback() {
    fetch('/cart.js')
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        document.querySelectorAll('.cart-count-bubble, [data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
      })
      .catch(function () {});
  }

  /* ----------------------------------------------------------- */
  /* 3. Button visual states                                      */
  /* ----------------------------------------------------------- */

  function setButtonState(button, state) {
    if (!button) return;

    if (state === 'loading') {
      button.dataset.originalText = button.dataset.originalText || button.textContent;
      button.dataset.state = 'loading';
      button.disabled = true;
      button.innerHTML =
        '<span class="comp-cstm-spinner" aria-hidden="true"></span>' +
        '<span class="comp-cstm-visually-hidden">Adding to cart</span>';
    } else if (state === 'added') {
      button.dataset.state = 'added';
      button.textContent = 'ADDED ✓';
      setTimeout(function () {
        button.dataset.state = 'default';
        button.disabled = false;
        button.textContent = button.dataset.originalText;
      }, 1500);
    } else {
      button.dataset.state = 'default';
      button.disabled = false;
      button.textContent = button.dataset.originalText;
    }
  }
})();